import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

// Dynamic import to avoid bundling if not needed
const importPasskey = () => import("@simplewebauthn/browser");

export default function Security() {
  const { user } = useAuth();
  const [totp, setTotp] = useState({ enabled: false, pending: false });
  const [setupData, setSetupData] = useState(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [passkeys, setPasskeys] = useState([]);
  const [newPkName, setNewPkName] = useState("Passkey");
  const [busy, setBusy] = useState(null);

  const loadAll = async () => {
    if (!user) return;
    try {
      const [t, p] = await Promise.all([
        api.get("/auth/2fa/status"),
        api.get("/auth/passkeys/list"),
      ]);
      setTotp(t.data);
      setPasskeys(p.data.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadAll(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const start2FA = async () => {
    setBusy("setup");
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
      toast.success("Scan the QR with Google Authenticator / Authy / 1Password");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Setup failed");
    } finally { setBusy(null); }
  };

  const confirm2FA = async () => {
    setBusy("confirm");
    try {
      await api.post("/auth/2fa/confirm", { code: confirmCode.trim() });
      toast.success("2FA enabled!");
      setSetupData(null); setConfirmCode(""); loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally { setBusy(null); }
  };

  const disable2FA = async () => {
    setBusy("disable");
    try {
      await api.post("/auth/2fa/disable", { code: disableCode.trim() });
      toast.success("2FA disabled");
      setDisableCode(""); loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disable failed");
    } finally { setBusy(null); }
  };

  const registerPasskey = async () => {
    setBusy("pk-register");
    try {
      const { startRegistration } = await importPasskey();
      const { data: options } = await api.post("/auth/passkeys/register/begin");
      const credential = await startRegistration({ optionsJSON: options });
      await api.post("/auth/passkeys/register/finish", { credential, name: newPkName || "Passkey" });
      toast.success("Passkey registered!");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Passkey registration failed");
    } finally { setBusy(null); }
  };

  const deletePasskey = async (id) => {
    if (!confirm("Delete this passkey?")) return;
    try {
      await api.delete(`/auth/passkeys/${id}`);
      toast.success("Passkey removed");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (!user) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center" data-testid="security-signin-required">
        <div className="font-serif-elite text-3xl mb-2">Security &amp; Access</div>
        <div className="text-[color:var(--text-secondary)] text-sm">Sign in to manage 2FA and passkeys.</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-8" data-testid="security-page">
      <div className="border-b border-[color:var(--border-default)] pb-4">
        <div className="text-[10px] font-mono-data text-[#FFD700] tracking-widest mb-2">◆ SECURITY &amp; ACCESS</div>
        <h1 className="font-serif-elite text-4xl md:text-5xl font-bold tracking-tight">Security</h1>
        <p className="text-[color:var(--text-secondary)] text-sm mt-2">Passkeys, TOTP 2-factor authentication, and connected accounts.</p>
      </div>

      {/* 2FA */}
      <section data-testid="section-2fa">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-[11px] font-mono-data tracking-widest text-white">TWO-FACTOR AUTHENTICATION (TOTP)</h2>
          <span className={`text-[10px] font-mono-data tracking-widest ${totp.enabled ? "text-bull" : "text-[color:var(--text-muted)]"}`}>
            {totp.enabled ? "◆ ENABLED" : "◆ DISABLED"}
          </span>
        </div>
        <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4">
          {!totp.enabled && !setupData && (
            <div>
              <div className="text-sm text-white mb-2">Add a second layer of security.</div>
              <div className="text-xs text-[color:var(--text-secondary)] mb-3">
                Uses Google Authenticator, Authy, 1Password, or any RFC 6238-compatible app.
              </div>
              <button
                data-testid="setup-2fa-btn"
                onClick={start2FA}
                disabled={busy === "setup"}
                className="bg-white text-black px-4 py-2 text-[11px] font-mono-data tracking-widest hover:opacity-90 disabled:opacity-50 transition-colors"
              >{busy === "setup" ? "…" : "SET UP 2FA"}</button>
            </div>
          )}

          {setupData && (
            <div data-testid="2fa-setup-panel">
              <div className="text-xs text-[color:var(--text-secondary)] mb-2">Scan the QR code with your authenticator app, then enter the 6-digit code below.</div>
              <div className="flex gap-4 items-start">
                <img src={`data:image/png;base64,${setupData.qr_png_base64}`} alt="TOTP QR" className="w-40 h-40 bg-white p-1" data-testid="2fa-qr" />
                <div className="flex-1">
                  <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest mb-1">MANUAL ENTRY</div>
                  <div className="font-mono-data text-xs text-gold break-all p-2 bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)]">{setupData.secret}</div>
                  <input
                    data-testid="2fa-confirm-input"
                    value={confirmCode}
                    onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6-digit code"
                    className="w-full mt-3 bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-2 py-2 text-lg font-mono-data text-white tracking-widest focus:outline-none focus:border-[color:var(--border-focus)]"
                  />
                  <button
                    data-testid="2fa-confirm-btn"
                    onClick={confirm2FA}
                    disabled={busy === "confirm" || confirmCode.length !== 6}
                    className="w-full mt-2 bg-white text-black py-2 text-[11px] font-mono-data tracking-widest hover:opacity-90 disabled:opacity-50 transition-colors"
                  >{busy === "confirm" ? "…" : "CONFIRM & ENABLE →"}</button>
                </div>
              </div>
            </div>
          )}

          {totp.enabled && (
            <div data-testid="2fa-enabled-panel">
              <div className="text-sm text-white mb-2">2FA is active on this account.</div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest">ENTER YOUR CODE TO DISABLE</div>
                  <input
                    data-testid="2fa-disable-input"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6-digit code"
                    className="w-full bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-2 py-2 text-sm font-mono-data text-white tracking-widest focus:outline-none focus:border-[color:var(--border-focus)]"
                  />
                </div>
                <button
                  data-testid="2fa-disable-btn"
                  onClick={disable2FA}
                  disabled={busy === "disable" || disableCode.length !== 6}
                  className="border border-bear text-bear px-3 py-2 text-[10px] font-mono-data tracking-widest hover:bg-bear hover:text-white disabled:opacity-50 transition-colors"
                >DISABLE</button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Passkeys */}
      <section data-testid="section-passkeys">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-[11px] font-mono-data tracking-widest text-white">PASSKEYS (WEBAUTHN)</h2>
          <span className="text-[10px] font-mono-data text-[color:var(--text-muted)]">{passkeys.length} REGISTERED</span>
        </div>
        <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)]">
          <div className="p-4 border-b border-[color:var(--border-default)]">
            <div className="text-xs text-[color:var(--text-secondary)] mb-2">Add a passkey for password-less login using Face ID, Touch ID, Windows Hello, or a hardware key.</div>
            <div className="flex gap-2">
              <input
                data-testid="passkey-name-input"
                value={newPkName}
                onChange={(e) => setNewPkName(e.target.value)}
                placeholder="Device name (e.g. iPhone)"
                className="flex-1 bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-2 py-2 text-sm text-white placeholder:text-[color:var(--text-muted)] focus:outline-none focus:border-[color:var(--border-focus)]"
              />
              <button
                data-testid="register-passkey-btn"
                onClick={registerPasskey}
                disabled={busy === "pk-register"}
                className="bg-white text-black px-4 py-2 text-[11px] font-mono-data tracking-widest hover:opacity-90 disabled:opacity-50 transition-colors"
              >{busy === "pk-register" ? "…" : "+ ADD PASSKEY"}</button>
            </div>
          </div>
          {passkeys.length === 0 ? (
            <div className="p-4 text-xs text-[color:var(--text-muted)] font-mono-data">No passkeys yet.</div>
          ) : (
            passkeys.map((pk) => (
              <div key={pk.id} data-testid={`passkey-${pk.id}`} className="flex items-center justify-between px-4 py-2 border-b border-[color:var(--border-default)] last:border-b-0">
                <div>
                  <div className="text-sm text-white">{pk.name}</div>
                  <div className="text-[10px] font-mono-data text-[color:var(--text-muted)]">
                    ADDED {(pk.created_at || "").slice(0, 10)} · LAST USED {pk.last_used ? pk.last_used.slice(0, 10) : "NEVER"}
                  </div>
                </div>
                <button
                  data-testid={`delete-passkey-${pk.id}`}
                  onClick={() => deletePasskey(pk.id)}
                  className="text-[10px] font-mono-data text-bear border border-bear px-2 py-1 hover:bg-bear hover:text-white transition-colors"
                >REVOKE</button>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
